import React, { useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Container, Divider,
  Paper, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { MdScience } from "react-icons/md";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";
import { ADMIN_API_URL } from "../variables/globals";

const PLANS = ["access", "pro", "elite"];
const PLAN_LABELS = { access: "ACCESS", pro: "PRO", elite: "ELITE" };

async function getToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}

async function fetchConfig() {
  const token = await getToken();
  const res = await fetch(`${ADMIN_API_URL}/trialConfig`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to load config");
  return data.config;
}

async function saveConfig(payload) {
  const token = await getToken();
  const res = await fetch(`${ADMIN_API_URL}/trialConfig`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to save config");
}

async function grantTrial(uid, planKey, months) {
  const token = await getToken();
  const res = await fetch(`${ADMIN_API_URL}/grantTrial`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ uid, planKey, months }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to grant trial");
  return data;
}

export default function TrialsView() {
  // Config state
  const [enabled, setEnabled]   = useState(false);
  const [months,  setMonths]    = useState({ access: 2, pro: 2, elite: 1 });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving,  setConfigSaving]  = useState(false);
  const [configMsg,     setConfigMsg]     = useState("");
  const [configErr,     setConfigErr]     = useState("");

  // Grant trial state
  const [grantEmail,  setGrantEmail]  = useState("");
  const [grantPlan,   setGrantPlan]   = useState("access");
  const [grantMonths, setGrantMonths] = useState(2);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantMsg,    setGrantMsg]    = useState("");
  const [grantErr,    setGrantErr]    = useState("");

  // Active trials
  const [trials,        setTrials]        = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(true);

  useEffect(() => {
    fetchConfig()
      .then(cfg => {
        if (cfg) {
          setEnabled(!!cfg.enabled);
          setMonths({
            access: cfg.plans?.access?.months ?? 2,
            pro:    cfg.plans?.pro?.months    ?? 2,
            elite:  cfg.plans?.elite?.months  ?? 1,
          });
        }
      })
      .catch(e => setConfigErr(e.message))
      .finally(() => setConfigLoading(false));

    getDocs(query(collection(db, "users"), where("subscriptionStatus", "==", "trial")))
      .then(snap => setTrials(snap.docs.map(d => ({ uid: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setTrialsLoading(false));
  }, []);

  async function handleSaveConfig() {
    setConfigSaving(true); setConfigMsg(""); setConfigErr("");
    try {
      await saveConfig({
        enabled,
        plans: {
          access: { months: Number(months.access) },
          pro:    { months: Number(months.pro) },
          elite:  { months: Number(months.elite) },
        },
      });
      setConfigMsg("Configuración guardada.");
    } catch (e) {
      setConfigErr(e.message);
    } finally {
      setConfigSaving(false);
    }
  }

  async function handleGrantTrial() {
    if (!grantEmail.trim()) { setGrantErr("Ingresa el correo del usuario."); return; }
    setGrantLoading(true); setGrantMsg(""); setGrantErr("");
    try {
      // Resolve email → uid via Firestore
      const snap = await getDocs(
        query(collection(db, "users"), where("email", "==", grantEmail.trim().toLowerCase()))
      );
      if (snap.empty) throw new Error("No se encontró ningún usuario con ese correo.");
      const uid = snap.docs[0].id;
      const result = await grantTrial(uid, grantPlan, Number(grantMonths));
      const until = new Date(result.trialEndsAt).toLocaleDateString("es", { day: "2-digit", month: "long", year: "numeric" });
      setGrantMsg(`Prueba otorgada hasta el ${until}.`);
      setGrantEmail("");
      // Refresh trials list
      const refresh = await getDocs(query(collection(db, "users"), where("subscriptionStatus", "==", "trial")));
      setTrials(refresh.docs.map(d => ({ uid: d.id, ...d.data() })));
    } catch (e) {
      setGrantErr(e.message);
    } finally {
      setGrantLoading(false);
    }
  }

  const card = {
    background: Colors.cardBackground,
    border: `1px solid ${Colors.border}`,
    borderRadius: 2,
    p: 3,
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <MdScience size={22} color={Colors.primary} />
        <Typography variant="h5" fontWeight={700} color={Colors.textPrimary}>
          Períodos de Prueba
        </Typography>
      </Stack>

      {/* ── Global config ── */}
      <Paper sx={card} elevation={0}>
        <Typography variant="subtitle1" fontWeight={600} color={Colors.textPrimary} mb={2}>
          Configuración global
        </Typography>

        {configLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Stack gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Switch
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
                color="secondary"
              />
              <Typography color={Colors.textSecondary} fontSize={14}>
                {enabled ? "Períodos de prueba habilitados" : "Períodos de prueba deshabilitados"}
              </Typography>
            </Stack>

            <Typography color={Colors.textSecondary} fontSize={13} mt={0.5}>
              Duración por plan (meses):
            </Typography>
            <Stack direction="row" gap={2} flexWrap="wrap">
              {PLANS.map(pk => (
                <TextField
                  key={pk}
                  label={PLAN_LABELS[pk]}
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 12 }}
                  value={months[pk]}
                  onChange={e => setMonths(m => ({ ...m, [pk]: e.target.value }))}
                  sx={{ width: 110 }}
                />
              ))}
            </Stack>

            <Stack direction="row" gap={2} alignItems="center">
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveConfig}
                disabled={configSaving}
              >
                {configSaving ? "Guardando..." : "Guardar configuración"}
              </Button>
              {configMsg && <Typography color="success.main" fontSize={13}>{configMsg}</Typography>}
              {configErr && <Typography color="error.main" fontSize={13}>{configErr}</Typography>}
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* ── Grant trial ── */}
      <Paper sx={{ ...card, mt: 3 }} elevation={0}>
        <Typography variant="subtitle1" fontWeight={600} color={Colors.textPrimary} mb={2}>
          Otorgar prueba manual
        </Typography>
        <Stack gap={2}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField
              label="Correo del usuario"
              size="small"
              value={grantEmail}
              onChange={e => setGrantEmail(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              select
              label="Plan"
              size="small"
              value={grantPlan}
              onChange={e => { setGrantPlan(e.target.value); setGrantMonths(months[e.target.value] ?? 2); }}
              SelectProps={{ native: true }}
              sx={{ width: 130 }}
            >
              {PLANS.map(pk => (
                <option key={pk} value={pk}>{PLAN_LABELS[pk]}</option>
              ))}
            </TextField>
            <TextField
              label="Meses"
              type="number"
              size="small"
              inputProps={{ min: 1, max: 12 }}
              value={grantMonths}
              onChange={e => setGrantMonths(e.target.value)}
              sx={{ width: 90 }}
            />
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGrantTrial}
              disabled={grantLoading}
            >
              {grantLoading ? "Otorgando..." : "Otorgar prueba"}
            </Button>
            {grantMsg && <Typography color="success.main" fontSize={13}>{grantMsg}</Typography>}
            {grantErr && <Typography color="error.main" fontSize={13}>{grantErr}</Typography>}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Active trials ── */}
      <Paper sx={{ ...card, mt: 3 }} elevation={0}>
        <Typography variant="subtitle1" fontWeight={600} color={Colors.textPrimary} mb={2}>
          Usuarios en período de prueba ({trials.length})
        </Typography>
        {trialsLoading ? (
          <CircularProgress size={24} />
        ) : trials.length === 0 ? (
          <Typography color={Colors.textSecondary} fontSize={13}>
            Ningún usuario en prueba actualmente.
          </Typography>
        ) : (
          <Stack gap={0} divider={<Divider sx={{ borderColor: Colors.border }} />}>
            {trials.map(t => {
              const exp = t.planExpiresAt?.toDate?.();
              const days = exp
                ? Math.max(0, Math.ceil((exp.getTime() - Date.now()) / 86400000))
                : null;
              return (
                <Box key={t.uid} sx={{ py: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography color={Colors.textPrimary} fontSize={14} fontWeight={500}>
                        {t.email || t.uid}
                      </Typography>
                      <Typography color={Colors.textSecondary} fontSize={12}>
                        Plan: {PLAN_LABELS[t.currentPlanKey] ?? t.currentPlanKey} ·{" "}
                        Otorgado por: {t.trialGrantedBy ?? "—"}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      {exp && (
                        <Typography color={days <= 3 ? Colors.error : Colors.textSecondary} fontSize={12}>
                          Vence: {exp.toLocaleDateString("es")}
                          {days !== null ? ` (${days}d)` : ""}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
